import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { Plus, X, BookOpen, Sparkles, Loader2, Search, Tag, Upload } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["research", "policy", "market", "competitor", "investor", "internal"];
const CAT_COLOR = {
  research: "#3B82F6", policy: "#A855F7", market: "#22C55E",
  competitor: "#F59E0B", investor: "#D4AF37", internal: "#94A3B8",
};

export default function Knowledge() {
  const [docs, setDocs] = useState([]);
  const [active, setActive] = useState(null);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ category: "research" });
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [asking, setAsking] = useState(false);
  const [filter, setFilter] = useState("");
  const fileInputRef = useRef(null);
  const [uploadCat, setUploadCat] = useState("research");

  const refresh = async () => {
    const r = await api.get("/knowledge");
    setDocs(r.data);
    if (!active && r.data[0]) setActive(r.data[0]);
  };
  useEffect(() => { refresh(); }, []);

  const save = async () => {
    if (!form.title || !form.content) { toast.error("Title and content required"); return; }
    try {
      await api.post("/knowledge", form);
      toast.success("Document indexed");
      setOpenNew(false); setForm({ category: "research" }); refresh();
    } catch (e) { toast.error("Failed"); }
  };

  const ask = async () => {
    if (!question.trim()) return;
    setAsking(true); setAnswer("");
    try {
      const r = await api.post("/knowledge/ask", {
        question,
        doc_ids: active ? [active.id] : [],
      });
      setAnswer(r.data.answer);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setAsking(false); }
  };

  const filtered = docs.filter((d) =>
    !filter || d.title.toLowerCase().includes(filter.toLowerCase()) || d.content.toLowerCase().includes(filter.toLowerCase())
  );

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", file.name.replace(/\.[^.]+$/, ""));
    fd.append("category", uploadCat);
    try {
      const r = await api.post("/knowledge/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setActive(r.data); refresh();
      toast.success("Document indexed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="knowledge-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2">// Intelligence Network</div>
          <h1 className="font-display text-4xl font-medium text-white tracking-tight">Knowledge & Research</h1>
          <p className="text-slate-400 mt-2 text-sm">Institutional memory with AI-powered synthesis.</p>
        </div>
        <div className="flex items-center gap-3">
          <input type="file" ref={fileInputRef} accept=".pdf,.docx,.txt,.md" className="hidden" onChange={uploadFile} data-testid="upload-file-input" />
          <select
            value={uploadCat}
            onChange={(e) => setUploadCat(e.target.value)}
            className="px-3 py-2 bg-[#0A111F] border border-white/10 text-white font-mono text-xs focus:border-[#D4AF37] outline-none"
            title="Category for next upload"
          >
            {CATEGORIES.map((c) => <option key={c} value={c}>{c.toUpperCase()}</option>)}
          </select>
          <button
            onClick={() => fileInputRef.current?.click()}
            data-testid="upload-doc-btn"
            className="px-4 py-2 flex items-center gap-2 font-medium text-sm border border-white/10 hover:border-[#D4AF37]/50 text-slate-300 hover:text-white"
          >
            <Upload className="w-4 h-4" /> Upload
          </button>
          <button
            onClick={() => setOpenNew(true)}
            data-testid="add-doc-btn"
            className="px-4 py-2 flex items-center gap-2 font-medium text-sm"
            style={{ background: "var(--gold-500)", color: "#050B14" }}
          >
            <Plus className="w-4 h-4" /> Add Text
          </button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Doc list */}
        <div className="col-span-12 lg:col-span-4 space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-500" strokeWidth={1.5} />
            <input
              data-testid="doc-search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search repository…"
              className="w-full pl-10 pr-3 py-2.5 bg-[#0A111F] border border-white/10 text-white font-mono text-sm focus:border-[#D4AF37] outline-none"
            />
          </div>
          <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-2">
            {filtered.map((d) => (
              <motion.div
                layout
                key={d.id}
                onClick={() => setActive(d)}
                data-testid={`doc-${d.id}`}
                className={`p-4 cursor-pointer border ${active?.id === d.id ? "border-[#D4AF37]/50 bg-[#10182B]" : "border-white/10 hover:border-[#D4AF37]/30 bg-[#0A111F]"}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-1.5 h-1.5" style={{ background: CAT_COLOR[d.category] }}></span>
                  <span className="label-eyebrow">{d.category}</span>
                </div>
                <div className="font-display text-sm text-white leading-tight">{d.title}</div>
                <div className="text-[11px] text-slate-500 mt-2 line-clamp-2">{d.content?.slice(0, 120)}</div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Active doc + Q&A */}
        <div className="col-span-12 lg:col-span-8 space-y-4">
          {active && (
            <div className="nexus-card p-6">
              <div className="label-eyebrow mb-1">// {active.category}</div>
              <h2 className="font-display text-2xl text-white">{active.title}</h2>
              <div className="mt-4 text-slate-300 text-sm leading-relaxed whitespace-pre-wrap nexus-prose">{active.content}</div>
            </div>
          )}

          {/* AI Q&A */}
          <div className="nexus-card p-6" data-testid="knowledge-qa">
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="w-4 h-4" style={{ color: "var(--gold-400)" }} strokeWidth={1.5} />
              <div>
                <div className="label-eyebrow">// Nexus Knowledge Synthesizer</div>
                <div className="font-display text-base text-white">Ask the repository</div>
              </div>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-3">
              <input
                data-testid="qa-input"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder={active ? `Question about "${active.title}"…` : "Question across repository…"}
                className="flex-1 px-3 py-2.5 bg-[#050B14] border border-white/10 text-white font-mono text-sm focus:border-[#D4AF37] outline-none"
              />
              <button
                type="submit"
                disabled={asking || !question.trim()}
                data-testid="qa-submit"
                className="px-5 py-2 font-medium flex items-center gap-2 disabled:opacity-50"
                style={{ background: "var(--gold-500)", color: "#050B14" }}
              >
                {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Synthesize
              </button>
            </form>
            {answer && (
              <motion.div
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                className="mt-5 border-l-2 pl-5 py-2" style={{ borderColor: "var(--gold-500)" }}
              >
                <div className="label-eyebrow mb-2">// SYNTHESIS</div>
                <div className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed nexus-prose">{answer}</div>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {openNew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setOpenNew(false)}>
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="bg-[#0A111F] border border-white/10 p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="doc-form"
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="label-eyebrow mb-1">// New Intelligence Record</div>
                <h2 className="font-display text-2xl text-white">Index a document</h2>
              </div>
              <button onClick={() => setOpenNew(false)} className="text-slate-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="label-eyebrow block mb-1.5">Title *</label>
                <input
                  data-testid="doc-title"
                  value={form.title || ""}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full px-3 py-2 bg-[#050B14] border border-white/10 text-white font-mono text-sm focus:border-[#D4AF37] outline-none"
                />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Category</label>
                <div className="grid grid-cols-3 gap-2">
                  {CATEGORIES.map((c) => (
                    <button
                      key={c}
                      onClick={() => setForm({ ...form, category: c })}
                      className={`px-3 py-2 text-xs font-mono tracking-widest uppercase border ${form.category === c ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 text-slate-400 hover:border-white/30"}`}
                    >{c}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Content *</label>
                <textarea
                  data-testid="doc-content"
                  rows={10}
                  value={form.content || ""}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  className="w-full px-3 py-2 bg-[#050B14] border border-white/10 text-white font-mono text-xs focus:border-[#D4AF37] outline-none resize-none"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setOpenNew(false)} className="px-5 py-2 border border-white/10 text-slate-300">Cancel</button>
              <button onClick={save} data-testid="save-doc-btn" className="px-5 py-2 font-medium" style={{ background: "var(--gold-500)", color: "#050B14" }}>Index Document</button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
